import { Capacitor } from '@capacitor/core'

/** Existing key — means setup completed (not soft "Later" dismiss). */
export const NATIVE_SETUP_COMPLETE_KEY = 'wortelogy-order-alert-setup-dismissed'
export const NATIVE_SETUP_SEEN_BUILD_KEY = 'wortelogy-native-setup-seen-build'
export const CHANNEL_SOUND_CONFIRM_KEY = 'wortelogy-order-alert-channel-confirmed'

function safeGet(key: string): string | null {
  if (typeof localStorage === 'undefined') return null
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function safeSet(key: string, value: string): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(key, value)
  } catch {
    // ignore
  }
}

function safeRemove(key: string): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.removeItem(key)
  } catch {
    // ignore
  }
}

/** Android versionCode / iOS build from @capacitor/app. */
export async function getNativeApkBuild(): Promise<string | null> {
  if (!Capacitor.isNativePlatform()) return null
  try {
    const { App } = await import('@capacitor/app')
    const info = await App.getInfo()
    const build = info.build?.trim()
    return build || null
  } catch {
    return null
  }
}

/**
 * If the installed APK build changed, clear completion flags so setup runs again.
 * Returns the current native build (or null on web / read failure).
 */
export async function prepareNativeSetupForCurrentApk(): Promise<string | null> {
  if (!Capacitor.isNativePlatform()) return null
  const build = await getNativeApkBuild()
  if (!build) return null
  const seen = safeGet(NATIVE_SETUP_SEEN_BUILD_KEY)
  if (seen !== build) {
    safeRemove(NATIVE_SETUP_COMPLETE_KEY)
    safeRemove(CHANNEL_SOUND_CONFIRM_KEY)
  }
  return build
}

/** Native-only: true when setup has not been completed for the current APK. */
export async function shouldShowNativeSetup(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false
  await prepareNativeSetupForCurrentApk()
  return safeGet(NATIVE_SETUP_COMPLETE_KEY) !== '1'
}

/** Mark first-run / post-APK setup complete for this native build. */
export async function markNativeSetupComplete(build?: string | null): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  const resolved = build ?? (await getNativeApkBuild())
  safeSet(NATIVE_SETUP_COMPLETE_KEY, '1')
  if (resolved) {
    safeSet(NATIVE_SETUP_SEEN_BUILD_KEY, resolved)
  }
}

/** @deprecated Prefer shouldShowNativeSetup — sync check without APK prepare. */
export function shouldShowOrderAlertSetup(): boolean {
  if (!Capacitor.isNativePlatform()) return false
  return safeGet(NATIVE_SETUP_COMPLETE_KEY) !== '1'
}
